import { useEffect, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { tokenHasTenant } from '@/core/auth';
import { isApiError } from '@/core/http';
import { ErrorState, FullscreenSpinner } from '@/core/ui';
import { useMe } from '../application/session';
import { BLOCKING_ERRORS, brandColor, type Tenant } from '../domain/user';
import { AccessBlocked } from './AccessBlocked';

/**
 * Porta da área logada.
 *
 * Faz o `GET /identity/me` — que provisiona o usuário no primeiro acesso —
 * antes de renderizar qualquer tela. Assim toda tela pode assumir que o
 * perfil e os papéis já estão em cache, e o bloqueio por suspensão vale
 * para o app inteiro, não para uma rota.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const me = useMe();

  useTenantBranding(me.data?.tenant);

  // Token rejeitado pela API (expirou sem renovar, issuer divergente):
  // descarta a sessão local e volta ao login.
  const unauthorized = isApiError(me.error) && me.error.status === 401;
  // Exceção: token válido, mas sem empresa (`tenant_id`). Acontece com quem
  // entra pela primeira vez com Google/Apple numa conta que ninguém liberou.
  // Voltar ao login não resolveria — o próximo login daria no mesmo.
  const withoutTenant = unauthorized && tokenHasTenant(auth.user?.access_token) === false;
  useEffect(() => {
    if (unauthorized && !withoutTenant) void auth.removeUser();
  }, [unauthorized, withoutTenant, auth]);

  if (withoutTenant) return <AccessBlocked {...NO_TENANT} />;
  if (me.isPending || unauthorized) return <FullscreenSpinner label="Preparando seu ambiente…" />;

  if (me.isError) {
    const blocking = isApiError(me.error) ? BLOCKING_ERRORS[me.error.code] : undefined;
    if (blocking) return <AccessBlocked {...blocking} />;
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} className="w-full max-w-md" />
      </div>
    );
  }

  return children;
}

const NO_TENANT = {
  title: 'Acesso ainda não liberado',
  message:
    'Sua conta foi reconhecida, mas ainda não está vinculada a nenhuma empresa no CentralOne. Peça ao RH ou ao administrador da plataforma para liberar o seu acesso e depois entre de novo.',
};

function useTenantBranding(tenant: Tenant | undefined) {
  const color = tenant ? brandColor(tenant) : undefined;
  useEffect(() => {
    if (!color) return;
    const root = document.documentElement.style;
    const vars = {
      '--color-brand-50': `color-mix(in oklch, ${color} 8%, white)`,
      '--color-brand-100': `color-mix(in oklch, ${color} 18%, white)`,
      '--color-brand-300': `color-mix(in oklch, ${color} 75%, white)`,
      '--color-brand-400': color,
      '--color-brand-500': `color-mix(in oklch, ${color} 88%, black)`,
      '--color-brand-600': `color-mix(in oklch, ${color} 76%, black)`,
      '--color-brand-700': `color-mix(in oklch, ${color} 64%, black)`,
      '--color-brand-900': `color-mix(in oklch, ${color} 35%, black)`,
    };
    for (const [k, v] of Object.entries(vars)) root.setProperty(k, v);
    return () => Object.keys(vars).forEach((k) => root.removeProperty(k));
  }, [color]);
}
